// File: src/components/modals/StudentDetailsModal.jsx
// Modal to display complete student details from multiple tables

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { X, User, Users, Calendar, Phone, Mail, MapPin, CreditCard, Clock, AlertCircle } from 'lucide-react';
import { studentAPI } from '../../services/api';

const StudentDetailsModal = ({ studentId, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (studentId) {
      loadStudentDetails();
    }
  }, [studentId]);

  const loadStudentDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await studentAPI.getCompleteStudentDetails(
        studentId,
        getAccessTokenSilently
      );

      if (response.success) {
        setStudentData(response.data);
      } else {
        setError(response.message || 'Failed to load student details');
      }
    } catch (err) {
      console.error('Error loading student details:', err);
      setError('Failed to load student details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    if (phone.startsWith('+44')) {
      return phone.replace(/(\+44)(\d{4})(\d{6})/, '$1 $2 $3');
    }
    return phone;
  };

  const formatTime = (time) => {
    if (!time) return '';
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${period}`;
    } catch {
      return time;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: '#10b981' },
      approve: { label: 'Pending Approval', color: '#f59e0b' },
      invited: { label: 'Invited', color: '#3b82f6' },
      inactive: { label: 'Inactive', color: '#6b7280' },
      passed: { label: 'Passed', color: '#2563eb' },
      deleted: { label: 'Deleted', color: '#ef4444' }
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span 
        className="status-badge-detail"
        style={{ backgroundColor: config.color }}
      >
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="loading-state">
            <Clock size={48} className="loading-icon" />
            <h3>Loading student details...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error || !studentData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Error</h2>
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <div className="error-state">
            <AlertCircle size={48} className="error-icon" />
            <p>{error || 'Student not found'}</p>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const { student, relationships, bookings } = studentData;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <User size={24} />
            <h2>Student Details</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Section 1: Basic Student Information */}
          <div className="detail-section">
            <div className="section-header">
              <User size={20} />
              <h3>Basic Information</h3>
            </div>
            <div className="section-content">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Full Name</span>
                  <span className="detail-value">{student.name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">{getStatusBadge(student.status)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle Type</span>
                  <span className="detail-value">{student.vehicleOption}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">
                    <Mail size={14} className="detail-icon" />
                    {student.email || 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">
                    <Phone size={14} className="detail-icon" />
                    {formatPhone(student.phone)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date of Birth</span>
                  <span className="detail-value">{formatDate(student.dateOfBirth)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Registration Date</span>
                  <span className="detail-value">{formatDate(student.registrationDate)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Licence Number</span>
                  <span className="detail-value">
                    <CreditCard size={14} className="detail-icon" />
                    {student.licenceNumber || 'N/A'}
                  </span>
                </div>
                <div className="student-detail-item-id">
                  <span className="detail-label">Student ID</span>
                  <span className="detail-value detail-value-mono">{student.studentId}</span>
                </div>
              </div>

              {/* Address Information */}
              <div className="address-section">
                <div className="address-block">
                  <h4><MapPin size={16} /> Pickup Address</h4>
                  <p>{student.pickupAddress || 'N/A'}</p>
                  <p className="postcode">{student.pickupPostcode || ''}</p>
                </div>
                <div className="address-block">
                  <h4><MapPin size={16} /> Dropoff Address</h4>
                  <p>{student.dropoffAddress || 'N/A'}</p>
                  <p className="postcode">{student.dropoffPostcode || ''}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Instructor Relationships  - Commented manageStudentServices.js Line 411
          <div className="detail-section">
            <div className="section-header">
              <Users size={20} />
              <h3>Instructor Relationships</h3>
            </div>
            <div className="section-content">
              {relationships && relationships.length > 0 ? (
                <div className="relationships-list">
                  {relationships.map((rel, index) => (
                    <div key={index} className="relationship-card">
                      <div className="relationship-header">
                        <span className="relationship-name">{rel.spName || 'Instructor'}</span>
                        <span className={`relationship-status ${rel.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                          {rel.status || 'N/A'}
                        </span>
                      </div>
                      <div className="relationship-details">
                        <div className="relationship-detail">
                          <Phone size={14} />
                          <span>{formatPhone(rel.spPhone)}</span>
                        </div>
                        <div className="relationship-detail">
                          <Mail size={14} />
                          <span>{rel.spEmail || 'N/A'}</span>
                        </div>
                        <div className="relationship-detail">
                          <Calendar size={14} />
                          <span>Since: {formatDate(rel.createdAt)}</span>
                        </div>
                      </div>
                      {rel.relationshipId && (
                        <div className="relationship-id">
                          ID: <span className="detail-value-mono">{rel.relationshipId}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-section">
                  <Users size={48} className="empty-icon" />
                  <p>No instructor relationships found</p>
                </div>
              )}
            </div>
          </div>
          {/* Section 2: Instructor Relationships */}


          {/* Section 3: Booking Slots */}
          <div className="detail-section">
            <div className="section-header">
              <Calendar size={20} />
              <h3>Booked Slots</h3>
            </div>
            <div className="section-content">
              {bookings && bookings.length > 0 ? (
                <div className="bookings-table">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Duration</th>
                        <th>Instructor</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking, index) => (
                        <tr key={index}>
                          <td>{formatDate(booking.date)}</td>
                          <td>
                            <Clock size={14} className="detail-icon" />
                            {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                          </td>
                          <td>
                            {booking.startTime && booking.endTime ? (
                              (() => {
                                const start = booking.startTime.split(':');
                                const end = booking.endTime.split(':');
                                const startMin = parseInt(start[0]) * 60 + parseInt(start[1]);
                                const endMin = parseInt(end[0]) * 60 + parseInt(end[1]);
                                const duration = endMin - startMin;
                                return `${duration} min`;
                              })()
                            ) : 'N/A'}
                          </td>
                          <td>{booking.providerName || 'N/A'}</td>
                          <td>
                            <span className={`booking-status ${booking.status === 'confirmed' ? 'status-confirmed' : ''}`}>
                              {booking.status || 'Confirmed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="bookings-summary">
                    <p><strong>Total Bookings:</strong> {bookings.length}</p>
                  </div>
                </div>
              ) : (
                <div className="empty-section">
                  <Calendar size={48} className="empty-icon" />
                  <p>No booking slots found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsModal;